import React, { useEffect, useMemo, useState } from "react";
import { Field, Form, Formik } from "formik";
import * as Icons from "react-feather";
import Modal from "components/Modal";
import { Loading, PersianDataPicker } from "components/form";
import { dateTimeToserver, toJalali } from "lib/helpers";
import { axiosInstance, handleErrors, handleSuccessMessages } from "store/auth";

const TRAFFIC_UNITS = ["B", "KB", "MB", "GB", "TB"];
const MAX_TRAFFIC = 8388607;
const EMPTY_META = { current_page: 1, last_page: 1, total: 0 };
const EMPTY_FILTERS = {
  date_from: "",
  date_to: "",
  unit: "",
  traffic_min: "",
  traffic_max: "",
};

const required = (value) =>
  value || value === 0 ? undefined : "این فیلد الزامی است";

const positiveNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return "این فیلد الزامی است";
  }

  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) {
    return "مقدار ترافیک باید عددی بزرگ‌تر از صفر باشد";
  }

  if (number > MAX_TRAFFIC) {
    return "مقدار واردشده بیش از حد مجاز است";
  }

  return undefined;
};

const buildUrl = (userId, page, filters) => {
  const params = new URLSearchParams({ page: String(page), perpage: "10" });

  if (filters.date_from) params.set("date_from", filters.date_from);
  if (filters.date_to) params.set("date_to", filters.date_to);
  if (filters.unit) params.set("unit", filters.unit);
  if (filters.traffic_min !== "") params.set("traffic_min", filters.traffic_min);
  if (filters.traffic_max !== "") params.set("traffic_max", filters.traffic_max);

  return `/panel/catalog-users/${userId}/traffic?${params.toString()}`;
};

const toServerDate = (value) =>
  value ? dateTimeToserver(value, "YYYY-MM-DD") : "";

const displayTraffic = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString("fa-IR") : value;
};

const validateFilters = (values) => {
  const errors = {};
  const hasTrafficRange = values.traffic_min !== "" || values.traffic_max !== "";

  if (hasTrafficRange && !values.unit) {
    errors.unit = "برای فیلتر مقدار، انتخاب واحد الزامی است";
  }

  if (
    values.traffic_min !== "" &&
    values.traffic_max !== "" &&
    Number(values.traffic_min) > Number(values.traffic_max)
  ) {
    errors.traffic_max = "حداکثر مقدار باید بزرگ‌تر یا مساوی حداقل مقدار باشد";
  }

  const dateFrom = toServerDate(values.date_from);
  const dateTo = toServerDate(values.date_to);
  if (dateFrom && dateTo && dateFrom > dateTo) {
    errors.date_to = "تاریخ پایان باید بعد از تاریخ شروع باشد";
  }

  return errors;
};

export default function CatalogTrafficModal({ show, user, onClose, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [records, setRecords] = useState([]);
  const [meta, setMeta] = useState(EMPTY_META);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [editing, setEditing] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const activeFiltersCount = Object.values(filters).filter(
    (value) => value !== "",
  ).length;

  const loadRecords = async (page = 1, requestedFilters = filters) => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const response = await axiosInstance.get(
        buildUrl(user.id, page, requestedFilters),
      );
      setRecords(response.data?.data || []);
      setMeta(response.data?.meta || EMPTY_META);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!show || !user?.id) return;

    setFilters(EMPTY_FILTERS);
    setEditing(null);
    setShowFilters(false);
    loadRecords(1, EMPTY_FILTERS);
    // Initial loading is intentionally limited to opening the modal or changing user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, user?.id]);

  const initialValues = useMemo(
    () => ({
      traffic_date: editing?.traffic_date
        ? new Date(`${editing.traffic_date}T00:00:00`)
        : new Date(),
      traffic: editing?.traffic ? Number(editing.traffic) : "",
      unit: editing?.unit || "GB",
      description: editing?.description || "",
    }),
    [editing],
  );

  const closeModal = () => {
    setRecords([]);
    setMeta(EMPTY_META);
    setFilters(EMPTY_FILTERS);
    setEditing(null);
    setShowFilters(false);
    onClose();
  };

  const saveTraffic = async (values, formikHelpers) => {
    const payload = {
      traffic_date: toServerDate(values.traffic_date),
      traffic: Number(values.traffic),
      unit: values.unit,
      description: values.description?.trim() || null,
    };

    setSaving(true);
    try {
      if (editing) {
        await axiosInstance.put(
          `/panel/catalog-users/${user.id}/traffic/${editing.id}`,
          payload,
        );
        handleSuccessMessages("رکورد ترافیک با موفقیت ویرایش شد");
      } else {
        await axiosInstance.post(
          `/panel/catalog-users/${user.id}/traffic`,
          payload,
        );
        handleSuccessMessages("رکورد ترافیک با موفقیت ثبت شد");
      }

      setEditing(null);
      formikHelpers.resetForm();
      await loadRecords(1);
      if (onChanged) onChanged();
    } catch (error) {
      handleErrors(error);
    } finally {
      setSaving(false);
      formikHelpers.setSubmitting(false);
    }
  };

  const deleteTraffic = async (record) => {
    if (!window.confirm(`رکورد ترافیک تاریخ ${record.traffic_date_jalali} حذف شود؟`)) {
      return;
    }

    setDeletingId(record.id);
    try {
      await axiosInstance.delete(
        `/panel/catalog-users/${user.id}/traffic/${record.id}`,
      );
      handleSuccessMessages("رکورد ترافیک با موفقیت حذف شد");
      const targetPage = records.length === 1 && meta.current_page > 1
        ? meta.current_page - 1
        : meta.current_page;
      await loadRecords(targetPage);
      if (onChanged) onChanged();
    } catch (error) {
      handleErrors(error);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Modal
      show={show}
      handleClose={closeModal}
      title={`ترافیک${user?.full_name ? ` - ${user.full_name}` : " کاتالوگ"}`}
      size="modal-xl"
    >
      <div className="modal-body p-5 sm:p-6">
        <div className="bg-gray-100 border border-gray-200 rounded-md p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-medium">
                {editing ? "ویرایش ترافیک" : "ثبت ترافیک"}
              </h3>
              <p className="text-xs text-gray-600 mt-1">ثبت روزانه؛ هر تاریخ فقط یک‌بار</p>
            </div>
            {editing && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setEditing(null)}
              >
                <Icons.X className="w-4 h-4 ml-1" />
                انصراف
              </button>
            )}
          </div>

          <Formik
            initialValues={initialValues}
            enableReinitialize
            onSubmit={saveTraffic}
          >
            {({ errors, isSubmitting, submitCount, touched }) => (
              <Form>
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-12 sm:col-span-6 lg:col-span-3 user-datepicker">
                    <Field
                      name="traffic_date"
                      label="تاریخ"
                      component={PersianDataPicker}
                      validate={required}
                      disable_today={false}
                    />
                  </div>
                  <div className="col-span-12 sm:col-span-6 lg:col-span-2">
                    <label className="block text-xs font-bold mb-2" htmlFor="traffic">
                      مقدار
                    </label>
                    <Field
                      as="input"
                      id="traffic"
                      name="traffic"
                      type="number"
                      placeholder="مثلاً 12.5"
                      className={`form-control w-full ${touched.traffic && errors.traffic ? "border-theme-6" : ""}`}
                      validate={positiveNumber}
                      min="0.0001"
                      max={MAX_TRAFFIC}
                      step="0.0001"
                    />
                  </div>
                  <div className="col-span-12 sm:col-span-4 lg:col-span-2">
                    <label className="block text-xs font-bold mb-2" htmlFor="unit">
                      واحد
                    </label>
                    <Field
                      as="select"
                      id="unit"
                      name="unit"
                      className="form-select w-full"
                      validate={required}
                    >
                      {TRAFFIC_UNITS.map((unit) => (
                        <option value={unit} key={unit}>{unit}</option>
                      ))}
                    </Field>
                  </div>
                  <div className="col-span-12 sm:col-span-8 lg:col-span-3">
                    <label className="block text-xs font-bold mb-2" htmlFor="description">
                      توضیحات <span className="font-normal text-gray-500">(اختیاری)</span>
                    </label>
                    <Field
                      as="input"
                      id="description"
                      name="description"
                      type="text"
                      placeholder="توضیح کوتاه"
                      className="form-control w-full"
                    />
                  </div>
                  <div className="col-span-12 lg:col-span-2">
                    <button
                      type="submit"
                      className="btn btn-primary w-full justify-center whitespace-nowrap"
                      disabled={isSubmitting || saving}
                    >
                      {saving ? (
                        <Loading />
                      ) : editing ? (
                        <><Icons.Save className="w-4 h-4 ml-2" /> ذخیره</>
                      ) : (
                        <><Icons.Plus className="w-4 h-4 ml-2" /> ثبت رکورد</>
                      )}
                    </button>
                  </div>
                </div>

                {submitCount > 0 && Object.keys(errors).length > 0 && (
                  <div className="text-theme-6 text-xs mt-2" role="alert">
                    {errors.traffic_date || errors.traffic || errors.unit}
                  </div>
                )}
              </Form>
            )}
          </Formik>
        </div>

        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <h3 className="font-medium">ترافیک‌ها</h3>
            <span className="bg-gray-100 text-gray-600 rounded px-2 py-1 text-xs mr-2">
              {meta.total.toLocaleString("fa-IR")} رکورد
            </span>
          </div>
          <button
            type="button"
            className={`btn btn-sm w-28 justify-center ${showFilters || activeFiltersCount ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => setShowFilters((visible) => !visible)}
            aria-expanded={showFilters}
          >
            <Icons.Filter className="w-4 h-4 ml-1" />
            فیلترها
            {activeFiltersCount > 0 && (
              <span className="mr-1">({activeFiltersCount.toLocaleString("fa-IR")})</span>
            )}
          </button>
        </div>

        {showFilters && (
          <div className="bg-gray-100 border border-gray-200 rounded-md p-4 mb-4">
            <Formik
              initialValues={filters}
              enableReinitialize
              validate={validateFilters}
              onSubmit={(values) => {
                const requestedFilters = {
                  date_from: toServerDate(values.date_from),
                  date_to: toServerDate(values.date_to),
                  unit: values.unit || "",
                  traffic_min: values.traffic_min,
                  traffic_max: values.traffic_max,
                };
                setFilters(requestedFilters);
                setShowFilters(false);
                loadRecords(1, requestedFilters);
              }}
            >
              {({ errors, resetForm, submitCount }) => (
                <Form>
                  {submitCount > 0 && Object.keys(errors).length > 0 && (
                    <div className="text-theme-6 text-sm mb-3" role="alert">
                      {Object.values(errors)[0]}
                    </div>
                  )}
                  <div
                    className="grid grid-cols-12 gap-4 items-end"
                    data-testid="traffic-filter-row"
                  >
                    <div className="col-span-12 lg:col-span-4 grid grid-cols-2 gap-3">
                      <div className="user-datepicker">
                        <Field
                          name="date_from"
                          label="از تاریخ"
                          component={PersianDataPicker}
                          disable_today
                        />
                      </div>
                      <div className="user-datepicker">
                        <Field
                          name="date_to"
                          label="تا تاریخ"
                          component={PersianDataPicker}
                          disable_today
                        />
                      </div>
                    </div>
                    <div className="col-span-12 sm:col-span-4 lg:col-span-2">
                      <label className="block text-xs font-bold mb-2" htmlFor="filter-unit">
                        واحد
                      </label>
                      <Field
                        as="select"
                        id="filter-unit"
                        name="unit"
                        className="form-select w-full"
                      >
                        <option value="">همه</option>
                        {TRAFFIC_UNITS.map((unit) => (
                          <option value={unit} key={unit}>{unit}</option>
                        ))}
                      </Field>
                    </div>
                    <div className="col-span-12 sm:col-span-8 lg:col-span-3 grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold mb-2" htmlFor="traffic-min">
                          حداقل
                        </label>
                        <Field
                          id="traffic-min"
                          name="traffic_min"
                          type="number"
                          min="0"
                          step="0.0001"
                          className="form-control w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold mb-2" htmlFor="traffic-max">
                          حداکثر
                        </label>
                        <Field
                          id="traffic-max"
                          name="traffic_max"
                          type="number"
                          min="0"
                          step="0.0001"
                          className="form-control w-full"
                        />
                      </div>
                    </div>
                    <div className="col-span-12 lg:col-span-3 flex gap-2">
                      <button
                        type="submit"
                        className="btn btn-primary flex-1 justify-center whitespace-nowrap"
                      >
                        <Icons.Filter className="w-4 h-4 ml-1" />
                        اعمال
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary flex-1 justify-center whitespace-nowrap"
                        onClick={() => {
                          resetForm();
                          setFilters(EMPTY_FILTERS);
                          setShowFilters(false);
                          loadRecords(1, EMPTY_FILTERS);
                        }}
                      >
                        <Icons.RotateCcw className="w-4 h-4 ml-1" />
                        پاک کردن
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        )}

        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
          <table className="table table-report mb-0">
            <thead>
              <tr>
                <th className="text-center whitespace-nowrap">تاریخ</th>
                <th className="text-center whitespace-nowrap">ترافیک</th>
                <th className="text-center whitespace-nowrap hidden sm:table-cell">توضیحات</th>
                <th className="text-center whitespace-nowrap">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={4}><Loading /></td></tr>
              )}
              {!loading && records.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-gray-500 py-5">رکوردی ثبت نشده است</td>
                </tr>
              )}
              {!loading && records.map((record) => (
                <tr key={record.id}>
                  <td className="text-center whitespace-nowrap">
                    {record.traffic_date_jalali || toJalali(record.traffic_date)}
                  </td>
                  <td className="text-center font-medium" dir="ltr">
                    {displayTraffic(record.traffic)} {record.unit}
                  </td>
                  <td
                    className="text-center text-gray-600 hidden sm:table-cell max-w-xs truncate"
                    title={record.description || ""}
                  >
                    {record.description || "-"}
                  </td>
                  <td>
                    <div className="flex justify-center gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm w-8 h-8 p-0 justify-center"
                        onClick={() => setEditing(record)}
                        aria-label="ویرایش"
                        title="ویرایش"
                      >
                        <Icons.Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm w-8 h-8 p-0 justify-center"
                        disabled={deletingId === record.id}
                        onClick={() => deleteTraffic(record)}
                        aria-label="حذف"
                        title="حذف"
                      >
                        <Icons.Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {meta.last_page > 1 && (
            <div className="flex justify-center items-center gap-3 border-t border-gray-200 py-3 px-4">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={loading || meta.current_page <= 1}
                onClick={() => loadRecords(meta.current_page - 1)}
              >
                قبلی
              </button>
              <span className="text-sm">
                صفحه {meta.current_page.toLocaleString("fa-IR")} از {meta.last_page.toLocaleString("fa-IR")}
              </span>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={loading || meta.current_page >= meta.last_page}
                onClick={() => loadRecords(meta.current_page + 1)}
              >
                بعدی
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
